<template>
    <v-container
        fluid
        class="pt-0"
    >
        <v-row
            justify-center
            align-center
            no-gutters
        >
            <v-col>
                <div class="content">
                    <v-col class="pt-0">
                        <div class="sticky-nav pt-7">
                            <div class="ml-4 mr-4 header-container d-flex">
                                <h1 class="page-header">
                                    Companies
                                </h1>
                                <v-text-field
                                    height="29"
                                    flat
                                    solo
                                    background-color="grey lighten-1"
                                    hide-details
                                    :append-icon="searchContent ? undefined : 'search'"
                                    label="Quick search"
                                    :value="searchContent"
                                    clearable
                                    class="quick-search"
                                    @input="setSearchContent"
                                />
                            </div>
                        </div>
                        <div>
                            <div class="company-listing-container">
                                <v-row
                                    class="pt-0 pb-0"
                                    style="margin: 0;"
                                >
                                    <div class="col-md-12 no-gutters pt-0 pb-0 sticky-table-header">
                                        <div class="d-flex company-listing-header listing-header-container">
                                            <div
                                                v-for="header in headers"
                                                :key="'list-header-' + header.value"
                                                :class="header.classes"
                                            >
                                                <v-btn
                                                    text
                                                    block
                                                    class="header-button"
                                                    @click.stop="() => {}"
                                                >
                                                    {{ header.text }}
                                                    <template>
                                                        <v-btn
                                                            icon
                                                            class="sort-button ml-2"
                                                            :class="{'is-sorted': sorting.column === header.value}"
                                                            small
                                                            :color="isSortedBy(header.value) && sorting.direction === 'asc' ? 'black' : ''"
                                                            @click="applyHeaderSort(header.value, 'asc')"
                                                        >
                                                            <v-icon
                                                                dark
                                                                small
                                                            >
                                                                mdi-arrow-up
                                                            </v-icon>
                                                        </v-btn>
                                                        <v-btn
                                                            icon
                                                            small
                                                            class="sort-button ml-2"
                                                            :class="{'is-sorted': isSortedBy(header.value)}"
                                                            :color="isSortedBy(header.value) && sorting.direction === 'desc' ? 'black' : ''"
                                                            @click.stop="applyHeaderSort(header.value, 'desc')"
                                                        >
                                                            <v-icon
                                                                dark
                                                                small
                                                            >
                                                                mdi-arrow-down
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                </v-btn>
                                            </div>
                                            <div class="button-column pt-2 pb-2 pl-4 pr-4">
                                                <router-link
                                                    v-if="canAddCompany"
                                                    :to="{name: 'addCompany'}"
                                                    style="text-decoration: none"
                                                >
                                                    <v-btn
                                                        color="accent"
                                                        block
                                                        class="header-button"
                                                    >
                                                        Add Company
                                                    </v-btn>
                                                </router-link>
                                            </div>
                                        </div>
                                    </div>

                                    <v-col
                                        v-for="company in items"
                                        :key="'company-listing-' + company.id"
                                        cols="12"
                                        class="company-listing-item pb-0 pt-0"
                                    >
                                        <v-card class="company-item">
                                            <div class="flex-grow-1 company-name-column pt-2 pb-2 pl-4 pr-4">
                                                {{ company.company_name }}
                                            </div>
                                            <div class="flex-grow-1 contact-person-column pt-2 pb-2 pl-4 pr-4">
                                                {{ company.contact_person }}
                                            </div>
                                            <div class="flex-grow-1 phone-column pt-2 pb-2 pl-4 pr-4">
                                                {{ company.phone_number }}
                                            </div>
                                            <div class="flex-grow-1 email-column pt-2 pb-2 pl-4 pr-4">
                                                {{ company.email_address }}
                                            </div>
                                            <div class="button-column pt-2 pb-2 pl-4 pr-4 text-right">
                                                <v-btn
                                                    v-if="canManageCompany"
                                                    icon
                                                    class="ma-auto text-capitalize"
                                                    @click="editCompany(company.id)"
                                                >
                                                    <v-icon
                                                        dark
                                                        class="edit"
                                                    >
                                                        edit
                                                    </v-icon>
                                                </v-btn>
                                                <v-btn
                                                    v-if="canDeleteCompany(company)"
                                                    icon
                                                    class="ma-auto text-capitalize"
                                                    @click="deleteCompany(company.id)"
                                                >
                                                    <v-icon dark>
                                                        mdi-delete
                                                    </v-icon>
                                                </v-btn>
                                            </div>
                                        </v-card>
                                    </v-col>
                                    <no-search-results v-if="noQuickSearchResult" />
                                </v-row>
                                <v-row class="col-md-12 no-gutters pt-0 pb-0">
                                    <x-paginator :value="pagination"
                                                 @input="setPagination"
                                    />
                                </v-row>
                            </div>
                        </div>
                    </v-col>
                </div>
            </v-col>
        </v-row>
        <delete-company
            v-model="deleteModal"
            :company="companyForDelete"
            @confirm="confirmDelete"
        />
    </v-container>
</template>

<script>
    import {mapState, mapMutations} from 'vuex';
    import {debounce} from 'lodash';
    import XPaginator from '../controls/paginator';
    import {$http, videoApiUrl} from '../../services/http';
    import qs from 'qs';
    import DeleteCompany from '../controls/modals/delete-company';
    import NoSearchResults from '../controls/no-search-result';
    import {toastSuccess} from '../../services/responseErrors';
    import Gate from '../../services/gate';

    export default {
        name: 'Companies',
        components: {DeleteCompany, XPaginator, NoSearchResults},
        data() {
            return {
                headers: [
                    {
                        text: 'Company Name',
                        value: 'company_name',
                        classes: ['flex-grow-1', 'company-name-column', 'pa-0', 'pt-2', 'pb-2']
                    },
                    {
                        text: 'Contact Person',
                        value: 'contact_person',
                        classes: ['flex-grow-1', 'contact-person-column', 'pa-0', 'pt-2', 'pb-2']
                    },
                    {
                        text: 'Phone Number',
                        value: 'phone_number',
                        classes: ['flex-grow-1', 'phone-column', 'pt-2', 'pa-0', 'pb-2']
                    },
                    {
                        text: 'E-mail Address',
                        value: 'email_address',
                        classes: ['flex-grow-1', 'email-column', 'pt-2', 'pa-0', 'pb-2']
                    },
                ],
                items: [],
                companyForDelete: {
                    company_name: '',
                },
                deleteModal: false,
                noQuickSearchResult: false,
            };
        },
        computed: {
            ...mapState('auth', {authUser: 'user'}),
            ...mapState('company-store', ['searchContent', 'sorting', 'pagination']),
            canAddCompany() {
                return Gate.allows(this.authUser, 'store-company');
            },
            canManageCompany() {
                return Gate.allows(this.authUser, 'manage-company');
            }
        },
        watch: {
            'pagination.currentPage': {
                handler(value, oldValue) {
                    if (value === oldValue) {
                        return;
                    }
                    this.items = [];
                    this.getPage();
                },
                deep: true,
            }
        },
        methods: {
            ...mapMutations('company-store', ['setSearch', 'setSorting', 'setPagination']),
            isSortedBy(column) {
                return column === this.sorting.column;
            },
            getPage() {
                $http.get(videoApiUrl('api/companies'), {
                    params: this.createRequest(),
                    paramsSerializer: params => decodeURIComponent(qs.stringify(params))
                }).then(response => {
                    const {data, total} = response.data;
                    this.setPagination({ total: total });
                    this.items = [...data];
                    this.$vuetify.goTo(0);

                    this.noQuickSearchResult = false;
                    if (this.items.length < 1) {
                        this.noQuickSearchResult = true;
                    }
                });
            },
            createRequest() {
                const request = {
                    sorting: {...this.sorting},
                    pagination: {
                        pageSize: this.pagination.itemPerPage,
                        current: this.pagination.currentPage
                    }
                };

                request.filters = {
                    quickSearch: this.searchContent
                };

                return request;

            },
            editCompany(companyID) {
                this.$router.push({name: 'editCompany', params: {'id': companyID}});
            },
            deleteCompany(companyID) {
                this.companyForDelete = this.items.filter(item => item.id === companyID)[0];
                this.deleteModal = true;
            },
            canDeleteCompany(company) {
                return this.canManageCompany && this.authUser.company !== company.id;
            },
            confirmDelete(company) {
                $http.delete(videoApiUrl('api/companies/' + company.id)).then(() => {
                    toastSuccess('Company deleted.');
                    this.setPagination({ currentPage: 1 });
                    this.getPage();
                });
            },
            applyHeaderSort(column, direction) {
                this.setSorting({
                    direction: direction,
                    column: column
                });
                this.setPagination({ currentPage: 1 });
                this.getPage();
            },
            setSearchContent: debounce(function (value) {
                this.setSearch(value);
                this.resetList();
            }, 300),
            resetList() {
                this.setPagination({ currentPage: 1 });
                this.items = [];
                this.getPage();
            },
        },
        mounted() {
            this.getPage();
        }
    };
</script>

<style scoped>
    .page-header {
        flex: 0 0 310px;
    }

    .quick-search {
        flex: 0 0 200px;
        margin-right: 12px;
    }

    @media screen and (max-width: 767px) {
        .page-header, .quick-search {
            flex: 1 1 50%;
        }
    }

    @media screen and (max-width: 500px) {
        .header-container {
            flex-wrap: wrap;
            margin-bottom: 10px;
        }

        .company-listing-header {
            flex-wrap: wrap;
        }

        .page-header, .quick-search {
            flex: 0 0 100%;
        }

        .company-item {
            flex-wrap: wrap;
            height: auto !important;
        }
    }

    .sticky-nav {
        overflow: hidden;
        position: sticky;
        position: -webkit-sticky;
        top: 64px;
        z-index: 999;
        background-color: #F5F5F5;
    }

    .sticky-table-header {
        position: sticky;
        top: 136px;
        z-index: 5;
        background-color: #F5F5F5;
    }
</style>
